import { useMemo, useState } from "react";

import type { AdminTrafficHour } from "@/gen/nodebase/v1/admin_pb";
import { Button } from "@/components/ui/button";
import { fmtBytes, fmtDateTime } from "@/lib/format";

type Measure = "bytes" | "connections";

type HourBin = {
  unix: number;
  workerBytes: bigint;
  edgeBytes: bigint;
  workerConnections: bigint;
  edgeConnections: bigint;
};

const WIDTH = 960;
const HEIGHT = 250;
const LEFT = 66;
const RIGHT = 14;
const TOP = 12;
const BOTTOM = 34;

// Missing ledger rows mean no metered traffic, not missing telemetry. Filling
// the selected range makes quiet periods visible instead of squeezing only
// busy hours together and implying continuous traffic.
function completeHours(rows: AdminTrafficHour[], days: number): HourBin[] {
  const byHour = new Map(rows.map((row) => [Number(row.windowStartUnix), row]));
  const end = Math.floor(Date.now() / 3_600_000) * 3600;
  const count = days * 24;

  return Array.from({ length: count }, (_, index) => {
    const unix = end - (count - index - 1) * 3600;
    const row = byHour.get(unix);
    return {
      unix,
      workerBytes: (row?.workerUpBytes ?? 0n) + (row?.workerDownBytes ?? 0n),
      edgeBytes: (row?.edgeUpBytes ?? 0n) + (row?.edgeDownBytes ?? 0n),
      workerConnections: row?.workerConnections ?? 0n,
      edgeConnections: row?.edgeConnections ?? 0n,
    };
  });
}

function metric(bin: HourBin, measure: Measure): [bigint, bigint] {
  return measure === "bytes"
    ? [bin.workerBytes, bin.edgeBytes]
    : [bin.workerConnections, bin.edgeConnections];
}

function formatMetric(value: bigint, measure: Measure): string {
  if (measure === "connections") return `${String(value)} connections`;
  return value === 0n ? "0 B" : fmtBytes(value);
}

function axisHour(unix: number, days: number): string {
  const date = new Date(unix * 1000);
  if (days === 1) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function HourlyTrafficChart({
  rows,
  days,
}: {
  rows: AdminTrafficHour[];
  days: number;
}) {
  const [measure, setMeasure] = useState<Measure>("bytes");
  const [activeUnix, setActiveUnix] = useState<number | null>(null);
  const bins = useMemo(() => completeHours(rows, days), [rows, days]);
  const totals = bins.map((bin) => {
    const [worker, edge] = metric(bin, measure);
    return worker + edge;
  });
  const max = totals.reduce(
    (highest, value) => (value > highest ? value : highest),
    0n,
  );
  const peakIndex = totals.reduce(
    (highest, value, index) => (value > totals[highest] ? index : highest),
    0,
  );
  const selectedIndex = bins.findIndex((bin) => bin.unix === activeUnix);
  const activeIndex = activeUnix === null || selectedIndex < 0 ? peakIndex : selectedIndex;
  const active = bins[activeIndex];
  const [activeWorker, activeEdge] = metric(active, measure);
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const cellWidth = plotWidth / bins.length;
  const barWidth = Math.max(0.7, cellWidth - Math.min(1.5, cellWidth * 0.2));
  const heightFor = (value: bigint) =>
    max === 0n ? 0 : (Number(value) / Number(max)) * plotHeight;
  const tickIndexes = Array.from(
    new Set([0, Math.floor((bins.length - 1) / 2), bins.length - 1]),
  );

  return (
    <div
      className="mb-4 rounded-lg border bg-card p-3 sm:p-4"
      data-testid="hourly-chart"
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium">
            {activeUnix === null ? "Peak hour" : "Selected hour"}: {fmtDateTime(active.unix)}
          </div>
          <div
            className="text-xs text-muted-foreground"
            data-testid="hourly-chart-detail"
          >
            {formatMetric(activeWorker + activeEdge, measure)} total · Worker {formatMetric(activeWorker, measure)} · Edge {formatMetric(activeEdge, measure)}
          </div>
        </div>
        <div className="flex items-center gap-1" aria-label="Hourly chart measure">
          <Button
            size="sm"
            variant={measure === "bytes" ? "secondary" : "ghost"}
            onClick={() => setMeasure("bytes")}
          >
            Traffic
          </Button>
          <Button
            size="sm"
            variant={measure === "connections" ? "secondary" : "ghost"}
            onClick={() => setMeasure("connections")}
          >
            Connections
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-blue-500" /> Worker egress
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-emerald-500" /> Managed Edge Exit
        </span>
      </div>
      <svg
        className="mt-2 h-auto w-full overflow-visible"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Hourly ${measure === "bytes" ? "traffic" : "connection"} chart for the last ${days * 24} hours`}
        onMouseLeave={() => setActiveUnix(null)}
      >
        {[0, 0.5, 1].map((ratio) => {
          const y = TOP + plotHeight * (1 - ratio);
          const value = BigInt(Math.round(Number(max) * ratio));
          return (
            <g key={ratio}>
              <line
                x1={LEFT}
                x2={WIDTH - RIGHT}
                y1={y}
                y2={y}
                className="stroke-border"
                strokeDasharray={ratio === 0 ? undefined : "3 4"}
              />
              <text
                x={LEFT - 8}
                y={y + 4}
                textAnchor="end"
                className="fill-muted-foreground text-[11px]"
              >
                {formatMetric(value, measure).replace(" connections", "")}
              </text>
            </g>
          );
        })}
        {bins.map((bin, index) => {
          const [worker, edge] = metric(bin, measure);
          const workerHeight = heightFor(worker);
          const edgeHeight = heightFor(edge);
          const x = LEFT + index * cellWidth + (cellWidth - barWidth) / 2;
          const workerY = TOP + plotHeight - workerHeight;
          const edgeY = workerY - edgeHeight;
          const selected = active.unix === bin.unix;
          return (
            <g key={bin.unix}>
              <title>
                {fmtDateTime(bin.unix)}: Worker {formatMetric(worker, measure)}, Edge {formatMetric(edge, measure)}
              </title>
              <rect
                x={x}
                y={workerY}
                width={barWidth}
                height={workerHeight}
                className="fill-blue-500"
                opacity={selected ? 1 : 0.78}
              />
              <rect
                x={x}
                y={edgeY}
                width={barWidth}
                height={edgeHeight}
                className="fill-emerald-500"
                opacity={selected ? 1 : 0.78}
              />
              <rect
                x={LEFT + index * cellWidth}
                y={TOP}
                width={cellWidth}
                height={plotHeight}
                fill="transparent"
                onMouseEnter={() => setActiveUnix(bin.unix)}
                onTouchStart={() => setActiveUnix(bin.unix)}
              />
            </g>
          );
        })}
        {tickIndexes.map((index) => (
          <text
            key={index}
            x={LEFT + index * cellWidth + cellWidth / 2}
            y={HEIGHT - 8}
            textAnchor={
              index === 0 ? "start" : index === bins.length - 1 ? "end" : "middle"
            }
            className="fill-muted-foreground text-[11px]"
          >
            {axisHour(bins[index].unix, days)}
          </text>
        ))}
      </svg>
      <p className="mt-1 text-xs text-muted-foreground">
        Hour buckets are complete across the selected range; gaps are zero traffic. Times follow this browser.
      </p>
    </div>
  );
}
